"use client";

import React from "react";
import UnifiedCheckout from "@/components/payments/UnifiedCheckout";
import CreditSuccessModal from "@/components/credits/CreditSuccessModal";
import FeedbackModal from "@/components/credits/FeedbackModal";
import SocialShareModal from "@/components/social-share/SocialShareModal";
import { CreateErrorToast, CreateWarningToast } from "@/components/create";
import type { SocialShareDesignContext } from "@/types/socialShare";
import {
  CreateCreditModal,
  type CreditModalContext,
} from "./CreateCreditModal";

interface CreatePageModalsProps {
  // Error & warning toasts
  errorMsg: string | null;
  onErrorClose: () => void;
  /** When provided, shows "Try again" button on error toast */
  onErrorRetry?: () => void;
  warnings: Array<{ variation: number; reason: string; error?: string }>;
  partialFailure: boolean;
  onWarningsClose: () => void;

  // Credit modal
  showCreditModal: boolean;
  onCreditModalClose: () => void;
  /**
   * Called when the modal is closed via the X button, Escape key, or overlay
   * click. Should dismiss the modal without submitting.
   */
  onCreditModalDismiss?: () => void;
  creditModalContext: CreditModalContext;
  creditPurchaseLoading: boolean;
  checkoutRecoveryCredits?: number | null;
  onPurchase: (
    credits: number,
    price: number,
    stripePriceId: string
  ) => void | Promise<void>;
  onFeedbackClick: () => void;
  onSocialShareClick: () => void;
  hasSubmittedFeedback: boolean | null;
  hasShared: boolean | null;
  hasImage: boolean;
  imageUrl?: string | null;
  hasShareablePastDesigns: boolean | null;
  canShowFeedbackReward: boolean;
  canEarnRewards: boolean;
  isAuthenticated: boolean;
  onSignIn: (credits?: number, offerToken?: string) => string | void;
  onSignUp?: () => string | void;
  onGoogleContinue?: (credits: number, offerToken?: string) => string | void;
  onCreateAccountAndBuy?: (params: {
    email: string;
    password: string;
    credits: number;
    price: number;
    stripePriceId: string;
  }) => Promise<{
    ok: boolean;
    error?: string;
    requiresConfirmation?: boolean;
  }>;
  pendingCreditConfirmation?: {
    email: string;
    credits: number;
  } | null;

  // Checkout & success
  showCreditCheckout: boolean;
  onCreditCheckoutClose: () => void;
  onCreditSuccess: (data: {
    type: string;
    credits?: number;
    transactionId?: string;
  }) => void;
  creditConfig: {
    type: "credit" | "ticket";
    priceId?: string;
    credits?: number;
    amount: number;
    [key: string]: unknown;
  } | null;
  showCreditSuccess: boolean;
  onCreditSuccessClose: () => void;
  creditSuccessData: { credits: number; transactionId: string } | null;

  // Feedback & social
  showFeedbackModal: boolean;
  onFeedbackModalClose: () => void;
  onFeedbackSuccess: () => void;
  showSocialShareModal: boolean;
  onSocialShareModalClose: () => void;
  onSocialShareSuccess: () => void;
  socialShareImageUrl?: string | null;
  socialShareBackImageUrl?: string | null;
  socialShareDesignContext?: SocialShareDesignContext | null;
}

/**
 * Shared modals for create page - used in both chat and split layout modes.
 * Uses shadcn Dialog via CreateCreditModal.
 */
export function CreatePageModals({
  errorMsg,
  onErrorClose,
  onErrorRetry,
  warnings,
  partialFailure,
  onWarningsClose,
  showCreditModal,
  onCreditModalClose,
  onCreditModalDismiss,
  creditModalContext,
  creditPurchaseLoading,
  checkoutRecoveryCredits,
  onPurchase,
  onFeedbackClick,
  onSocialShareClick,
  hasSubmittedFeedback,
  hasShared,
  hasImage,
  imageUrl = null,
  hasShareablePastDesigns,
  canShowFeedbackReward,
  canEarnRewards,
  isAuthenticated,
  onSignIn,
  onSignUp,
  onGoogleContinue,
  onCreateAccountAndBuy,
  pendingCreditConfirmation = null,
  showCreditCheckout,
  onCreditCheckoutClose,
  onCreditSuccess,
  creditConfig,
  showCreditSuccess,
  onCreditSuccessClose,
  creditSuccessData,
  showFeedbackModal,
  onFeedbackModalClose,
  onFeedbackSuccess,
  showSocialShareModal,
  onSocialShareModalClose,
  onSocialShareSuccess,
  socialShareImageUrl = null,
  socialShareBackImageUrl = null,
  socialShareDesignContext = null,
}: CreatePageModalsProps) {
  return (
    <>
      <CreateErrorToast
        errorMsg={errorMsg}
        onClose={onErrorClose}
        onRetry={onErrorRetry}
      />
      <CreateWarningToast
        warnings={warnings}
        partialFailure={partialFailure}
        onClose={onWarningsClose}
      />

      <CreateCreditModal
        open={showCreditModal}
        onClose={onCreditModalClose}
        onDismiss={onCreditModalDismiss}
        context={creditModalContext}
        creditPurchaseLoading={creditPurchaseLoading}
        checkoutRecoveryCredits={checkoutRecoveryCredits}
        onPurchase={onPurchase}
        onFeedbackClick={onFeedbackClick}
        onSocialShareClick={onSocialShareClick}
        hasSubmittedFeedback={hasSubmittedFeedback}
        hasShared={hasShared}
        hasImage={!!hasImage}
        imageUrl={imageUrl}
        hasShareablePastDesigns={hasShareablePastDesigns}
        canShowFeedbackReward={canShowFeedbackReward}
        canEarnRewards={canEarnRewards}
        isAuthenticated={isAuthenticated}
        onCreateAccountAndBuy={onCreateAccountAndBuy}
        onSignIn={onSignIn}
        onSignUp={onSignUp}
        onGoogleContinue={onGoogleContinue}
        pendingConfirmation={pendingCreditConfirmation}
      />

      {creditConfig && (
        <UnifiedCheckout
          isOpen={showCreditCheckout}
          onClose={onCreditCheckoutClose}
          onSuccess={onCreditSuccess}
          config={creditConfig}
          title="Buy credits"
          subtitle="Opening secure checkout for your credits…"
        />
      )}

      {creditSuccessData && (
        <CreditSuccessModal
          isOpen={showCreditSuccess}
          onClose={onCreditSuccessClose}
          credits={creditSuccessData.credits}
          transactionId={creditSuccessData.transactionId}
        />
      )}

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={onFeedbackModalClose}
        onSuccess={onFeedbackSuccess}
      />

      <SocialShareModal
        isOpen={showSocialShareModal}
        onClose={onSocialShareModalClose}
        onSuccess={onSocialShareSuccess}
        imageUrl={socialShareImageUrl}
        backImageUrl={socialShareBackImageUrl}
        designContext={socialShareDesignContext}
        entryPoint={socialShareImageUrl ? "create_page" : "past_work"}
      />
    </>
  );
}
